import React, { useContext, useEffect, useState } from 'react';
import Input from '@ferris/react/components/forms/Input';
import { Loader } from '@ferris/react/components/loader';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import { useLazyQuery, gql } from '@apollo/client';
import { TrackingContext } from '@vrbo/react-event-tracking';
import PhoneInputWrapper from '../../Input/PhoneInputWrapper';
import withPageFlow from '../../PageFlow/PageFlow';
import TranslationUtil from '../../../utils/TranslationUtil';
import LeadActions from '../../../actions/lead';
import MissingDetailsSubmit from './MissingDetailsSubmit';
import useUpdateContact from './useUpdateContact';
import { createOutcomeEvent, createPageViewEvent, setBrandCustomerId } from '../../../utils/EventProviders/EgClickstreamUtil';
import { getCookie } from '../../../utils/CookieUtil';
import log from '../../../utils/ClientLogger';

export const FETCH_PROFILE_PHONE_NUMBER = gql`
query fetchProfilePhoneNumber {
    fetchProfilePhoneNumber {
        phoneNumber,
        phoneCountryCode,
        phoneCountryCodeDigits
    }
}
`;

export function MissingDetails(props) {
    const {
        setNewSupplier,
        setPassword,
        referrer,
        setPublicUuid,
        setAutoAuthUrl,
        pageFlow,
        title,
        passwordLabel,
        phoneValidation,
        ctaLabel,
        hasPhoneNumber,
        hasPasswordSet,
        password,
        phoneNumber,
        phoneCountryCode,
        phoneCountryCodeDigits,
        locale,
        fingerprint,
        duaid,
        setPhoneNumber,
        setPhoneCountryCode,
        setPhoneCountryCodeDigits,
        setPhoneValidation
    } = props;
    const context = useContext(TrackingContext);
    const { trigger } = context;
    const hasUsablePhone = Boolean(
        hasPhoneNumber && phoneNumber?.trim() && phoneCountryCode && phoneCountryCodeDigits
    );
    const [recovering, setRecovering] = useState(!hasUsablePhone);
    const [phoneRecovered, setPhoneRecovered] = useState(false);
    const [fetchProfilePhoneNumber] = useLazyQuery(FETCH_PROFILE_PHONE_NUMBER);
    useEffect(() => {
        window.scrollTo(0, 0);
        trigger('pageLoad', { pageName: 'partner-qualifier: MissingDetails', referrer });
        trigger('pageview', createPageViewEvent('missing_details'));
        return () => {
            trigger('pageUnload', { pageName: 'partner-qualifier: MissingDetails', referrer });
        };
    });

    const onCompleted = (data) => {
        const { type: contactType, vrboContact } = data.updateContact;
        switch (contactType) {
        case 'FRBO':
            // May need to update the below structure once the gql changes are merged
            setPublicUuid(vrboContact.userPublicUuid);
            setBrandCustomerId(vrboContact.userPublicUuid);
            setNewSupplier(vrboContact.newSupplier);
            setAutoAuthUrl(vrboContact.autoAuthUrl);
            if (vrboContact.newSupplier) {
                trigger('userAccountCreated', { publicuuid: vrboContact.userPublicUuid });
                trigger('outcome', createOutcomeEvent('user_account_creation.succeeded', 'MissingDetails', 'UpgradeAccount'));
            } else {
                trigger('outcome', createOutcomeEvent('user_account_upgraded.succeeded', 'MissingDetails', 'UpgradeAccount'));
            }
            pageFlow.next();
            break;

        default:
            throw new Error(`Unsupported contactType '${contactType}' returned by service`);
        }
    };

    const onRecoveryError = () => {
        setPhoneRecovered(false);
        setRecovering(false);
    };

    const { submitContact } = useUpdateContact({ onCompleted, onError: onRecoveryError });

    const logRecovery = (outcome) => {
        log(['info'], 'missing_details_phone_recovery', {
            outcome,
            hasPasswordSet: Boolean(hasPasswordSet)
        });
        trigger('outcome', {
            category: 'property_creation',
            action: `phone_recovery_${outcome}`,
            label: 'MissingDetails'
        });
    };

    useEffect(() => {
        if (!recovering) return undefined;

        let cancelled = false;

        const recoverPhoneNumber = async () => {
            const response = await fetchProfilePhoneNumber({ fetchPolicy: 'no-cache' });
            const {
                phoneNumber: profilePhoneNumber,
                phoneCountryCode: profilePhoneCountryCode,
                phoneCountryCodeDigits: profilePhoneCountryCodeDigits
            } = response?.data?.fetchProfilePhoneNumber || {};

            if (cancelled) return;

            const recovered = Boolean(
                profilePhoneNumber?.trim()
                && profilePhoneCountryCode
                && profilePhoneCountryCodeDigits
            );

            if (!recovered) {
                logRecovery(response?.error ? 'failed' : 'unavailable');
                setRecovering(false);
                return;
            }

            setPhoneNumber(profilePhoneNumber);
            setPhoneCountryCode(profilePhoneCountryCode);
            setPhoneCountryCodeDigits(profilePhoneCountryCodeDigits);
            setPhoneValidation({
                isValid: true,
                validationService: { success: true, isValid: true }
            });
            setPhoneRecovered(true);

            if (!hasPasswordSet) {
                logRecovery('recovered_password_still_missing');
                setRecovering(false);
                return;
            }

            logRecovery('recovered_and_submitted');
            await submitContact({
                password,
                phoneCountryCode: profilePhoneCountryCode,
                phoneCountryCodeDigits: profilePhoneCountryCodeDigits,
                phoneNumber: profilePhoneNumber,
                locale,
                fingerprint,
                duaid
            });
        };

        recoverPhoneNumber();

        return () => {
            cancelled = true;
        };
    }, []);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };
    const validPhone = phoneValidation.isValid
    && (phoneValidation.validationService && phoneValidation.validationService.isValid);
    if (recovering) {
        return (
            <div className="ulx-missing-details ulx-form">
                <Loader visible />
            </div>
        );
    }

    return (
        <div className="ulx-missing-details ulx-form">
            <h2>{title}</h2>
            <div className="ulx-form__form fs-block">
                {!hasPhoneNumber && !phoneRecovered && <PhoneInputWrapper />}
                {!hasPasswordSet && (
                    <Input
                        id="password-input"
                        classes="password-input"
                        fullWidth
                        label={passwordLabel}
                        type="password"
                        isControlled
                        floatLabel
                        data-wdio="password-input"
                        value={password}
                        onChange={handlePasswordChange}
                        onBlur={handlePasswordChange}
                    />
                )}
                <div className="missing-details-submit">
                    <MissingDetailsSubmit
                        label={ctaLabel}
                        onCompleted={onCompleted}
                        onError={() => {}}
                        {...props}
                        validPhone={validPhone}
                    />
                </div>
            </div>
        </div>
    );
}

MissingDetails.propTypes = {
    title: PropTypes.string.isRequired,
    passwordLabel: PropTypes.string.isRequired,
    ctaLabel: PropTypes.string.isRequired,
    setPassword: PropTypes.func.isRequired,
    password: PropTypes.string.isRequired,
    passwordValidation: PropTypes.shape({
        isValid: PropTypes.bool.isRequired,
        error: PropTypes.string
    }).isRequired,
    hasPhoneNumber: PropTypes.bool.isRequired,
    hasPasswordSet: PropTypes.bool.isRequired,
    referrer: PropTypes.string.isRequired,
    setNewSupplier: PropTypes.func.isRequired,
    setPublicUuid: PropTypes.func.isRequired,
    setAutoAuthUrl: PropTypes.func.isRequired,
    setPhoneNumber: PropTypes.func.isRequired,
    setPhoneCountryCode: PropTypes.func.isRequired,
    setPhoneCountryCodeDigits: PropTypes.func.isRequired,
    setPhoneValidation: PropTypes.func.isRequired,
    phoneCountryCode: PropTypes.string.isRequired,
    phoneValidation: PropTypes.shape({
        validationService: PropTypes.shape({
            success: PropTypes.bool,
            isValid: PropTypes.bool
        }),
        isValid: PropTypes.bool,
        error: PropTypes.string
    }).isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    displayLocale: PropTypes.string.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    fingerprint: PropTypes.shape({
        orgId: PropTypes.string,
        deviceId: PropTypes.string
    }).isRequired,
    duaid: PropTypes.string.isRequired
};

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app, lead } = state;
    const { referrer, fingerprint } = app;
    const { password, missingInfo, passwordValidation } = lead;
    const {
        phoneValidation, phoneCountryCode, phoneNumber, phoneCountryCodeDigits
    } = lead;
    const { hasPhoneNumber, hasPasswordSet } = missingInfo || {};
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const duaid = getCookie('DUAID');
    return ({
        title: translations.getTranslation('lyp.leadForm.title'),
        passwordLabel: translations.getTranslation('lyp.leadForm.password.label'),
        ctaLabel: translations.getTranslation('Vrbo.cta.next'),
        password,
        hasPhoneNumber,
        hasPasswordSet,
        referrer,
        phoneCountryCode,
        phoneValidation,
        phoneCountryCodeDigits,
        phoneNumber,
        locale: app.locale.canonical,
        displayLocale: app.locale.display,
        fingerprint,
        duaid,
        passwordValidation
    });
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    setPassword: (password) => dispatch(LeadActions.setPassword(password)),
    setNewSupplier: (newSupplier) => dispatch(LeadActions.setNewSupplier(newSupplier)),
    setPublicUuid: (publicUuid) => dispatch(LeadActions.setPublicUuid(publicUuid)),
    setAutoAuthUrl: (autoAuthUrl) => dispatch(LeadActions.setAutoAuthUrl(autoAuthUrl)),
    setPhoneNumber: (number) => dispatch(LeadActions.setPhoneNumber(number)),
    setPhoneCountryCode: (code) => dispatch(LeadActions.setPhoneCountryCode(code)),
    setPhoneCountryCodeDigits: (code) => dispatch(LeadActions.setPhoneCountryCodeDigits(code)),
    setPhoneValidation: (result) => dispatch(LeadActions.setPhoneValidation(result))
});

const ConnectedMissingDetails = connect(mapStateToProps, mapDispatchToProps)(MissingDetails);
ConnectedMissingDetails.displayName = 'MissingDetails';
export default hot(module)(withPageFlow(ConnectedMissingDetails));
